import { format } from "date-fns";
import { toWords } from "number-to-words";
import useFormattingUtils from "../../../../../context/useFormattingUtils";
import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";

const DEFAULT_POSITIONS = {
  width: "203",
  height: "104",
  name: { x: 100, y: 100 },
  amount: { x: 600, y: 100 },
  amountInWords: { x: 100, y: 150 },
  date: { x: 600, y: 50 },
  logo: { x: 10, y: 10 },
  signature: { x: 600, y: 200 },
};


const getCurrencyNames = (currencyCode) => {
  try {
    const formatter = new Intl.NumberFormat("en", {
      style: "currency",
      currency: currencyCode,
      currencyDisplay: "name",
    });

    const parts = formatter.formatToParts(1);

    const currencyPart = parts.find((p) => p.type === "currency");

    if (!currencyPart) {
      return { main: "Currency", sub: "Subunit" };
    }

    const main = currencyPart.value;

    const subMap = {
      INR: "Paise",
      USD: "Cents",
      GBP: "Pence",
      EUR: "Cents",
    };

    return {
      main,
      sub: subMap[currencyCode] || "Subunit",
    };
  } catch {
    return { main: "Currency", sub: "Subunit" };
  }
};


const ChequeRenderer = forwardRef(
  ({ layout, chequeData, logo, signature, showPrint = false }, ref) => {
    const containerRef = useRef(null);

    const [positions, setPositions] = useState(DEFAULT_POSITIONS);
    const [dateFormat, setDateFormat] = useState("MM-dd-yyyy");
    const { formatCurrency, currency } = useFormattingUtils();
    /* ---------------- LOAD LAYOUT ---------------- */

    useEffect(() => {
      if (!layout) return;

      setPositions({
        width: layout.width || DEFAULT_POSITIONS.width,
        height: layout.height || DEFAULT_POSITIONS.height,

        name: {
          x: parseFloat(layout.name_x) || DEFAULT_POSITIONS.name.x,
          y: parseFloat(layout.name_y) || DEFAULT_POSITIONS.name.y,
        },

        amount: {
          x: parseFloat(layout.amount_x) || DEFAULT_POSITIONS.amount.x,
          y: parseFloat(layout.amount_y) || DEFAULT_POSITIONS.amount.y,
        },

        amountInWords: {
          x:
            parseFloat(layout.amount_in_words_x) ||
            DEFAULT_POSITIONS.amountInWords.x,
          y:
            parseFloat(layout.amount_in_words_y) ||
            DEFAULT_POSITIONS.amountInWords.y,
        },

        date: {
          x: parseFloat(layout.date_x) || DEFAULT_POSITIONS.date.x,
          y: parseFloat(layout.date_y) || DEFAULT_POSITIONS.date.y,
        },

        logo: {
          x: parseFloat(layout.logo_x) || DEFAULT_POSITIONS.logo.x,
          y: parseFloat(layout.logo_y) || DEFAULT_POSITIONS.logo.y,
        },

        signature: {
          x:
            parseFloat(layout.digital_sign_x) ||
            DEFAULT_POSITIONS.signature.x,
          y:
            parseFloat(layout.digital_sign_y) ||
            DEFAULT_POSITIONS.signature.y,
        },
      });

      setDateFormat(layout.date_format || "MM-dd-yyyy");
    }, [layout]);

    /* ---------------- PRINT HANDLER ---------------- */

    const print = (e) => {
      e?.preventDefault();
      if (!containerRef.current) return;

      const iframe = document.createElement("iframe");
      iframe.style.position = "absolute";
      iframe.style.top = "-9999px";
      iframe.style.left = "-9999px";

      document.body.appendChild(iframe);

      const iframeDoc =
        iframe.contentDocument || iframe.contentWindow.document;

      iframeDoc.open();

      iframeDoc.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Print Cheque</title>

            <style>
              body {
                margin: 0;
              }

              .cheque-print {
                width: ${positions.width}mm;
                height: ${positions.height}mm;
                background: white;
                position: relative;
              }

              .cheque-print * {
                box-sizing: border-box;
              }

              @media print {
                body {
                  margin: 0;
                }
              }
            </style>
          </head>

          <body>
            <div class="cheque-print">
              ${containerRef.current.innerHTML}
            </div>
          </body>
        </html>
      `);

      iframeDoc.close();

      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();

        document.body.removeChild(iframe);
      }, 400);
    };

    /* ---------------- EXPOSE PRINT TO PARENT ---------------- */

    useImperativeHandle(ref, () => ({
      print,
    }));

    /* ---------------- HELPERS ---------------- */

  const formatAmountInWords = (amount) => {
  if (!amount || isNaN(parseFloat(amount))) return "";

  const num = parseFloat(amount);

  const integerPart = Math.floor(num);
  const decimalPart = Math.round((num - integerPart) * 100);

  const integerWords = toWords(integerPart).replace(/\b\w/g, (l) =>
    l.toUpperCase()
  );

  const decimalWords =
    decimalPart > 0
      ? toWords(decimalPart).replace(/\b\w/g, (l) => l.toUpperCase())
      : "";

  const activeCurrency =
    chequeData?.currency ||
    currency ||
    layout?.currency_code ||
    layout?.currency ||
    "USD";

  const { main, sub } = getCurrencyNames(activeCurrency);

  let result = `${integerWords} ${main}`;

  if (decimalWords) {
    result += ` and ${decimalWords} ${sub}`;
  }

  result += " Only";

  return result;
};

    const formatDateValue = (dateStr) => {
      if (!dateStr) return "";

      try {
        return format(new Date(dateStr), dateFormat);
      } catch {
        return dateStr;
      }
    };

    /* ---------------- UI ---------------- */

    return (
      <div>
        {/* Preview Container */}
        <div
          ref={containerRef}
          style={{
            width: `${positions.width}mm`,
            height: `${positions.height}mm`,
            background: "#fff",
            border: "1px solid #ccc",
            position: "relative",
            margin: "20px auto",
            fontSize: "12px",
            fontFamily: "Arial, sans-serif",
          }}
        >
          {/* Name */}
          <div
            style={{
              position: "absolute",
              left: `${positions.name.x}px`,
              top: `${positions.name.y}px`,
              fontWeight: "bold",
            }}
          >
            {chequeData.name}
          </div>

          {/* Amount */}
          <div
            style={{
              position: "absolute",
              left: `${positions.amount.x}px`,
              top: `${positions.amount.y}px`,
              fontWeight: "bold",
            }}
          >
            {formatCurrency(chequeData.amount)}
          </div>

          {/* Amount In Words */}
          <div
            style={{
              position: "absolute",
              left: `${positions.amountInWords.x}px`,
              top: `${positions.amountInWords.y}px`,
              maxWidth: "400px",
              wordWrap: "break-word",
            }}
          >
            {formatAmountInWords(chequeData.amount)}
          </div>

          {/* Date */}
          <div
            style={{
              position: "absolute",
              left: `${positions.date.x}px`,
              top: `${positions.date.y}px`,
            }}
          >
            {formatDateValue(chequeData.date)}
          </div>

          {/* Logo */}
          {logo && (
            <img
              src={logo}
              alt="logo"
              style={{
                position: "absolute",
                left: `${positions.logo.x}px`,
                top: `${positions.logo.y}px`,
                width: "50px",
              }}
            />
          )}

          {/* Signature */}
          {signature && (
            <img
              src={signature}
              alt="signature"
              style={{
                position: "absolute",
                left: `${positions.signature.x}px`,
                top: `${positions.signature.y}px`,
                width: "80px",
              }}
            />
          )}
        </div>

        {/* Print Button (Optional) */}
        {showPrint && (
          <div style={{ textAlign: "right", marginTop: "10px" }}>
            <button
              type="button"
              onClick={print}
              style={{
                padding: "8px 16px",
                background: "#2e7d32",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Print Cheque
            </button>
          </div>
        )}
      </div>
    );
  }
);

export default ChequeRenderer;
